import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BASE_URL, API_ENDPOINTS } from "../../config/apiconfig";
import { toast } from "react-toastify";
import "../../style/ToastNotifications.css";
import { getCustomerById } from "../../api/customerApi";
import { crmLeadApi, crmOpportunityApi } from "../../api/crmApi";
import "../../style/CreateCustomer.css";
import AppDropdown from "../../components/AppDropdown";
import { getErrorMessage, getFieldErrors, mapApiFieldToFormField } from "../../utils/errorHandler";
import { FiChevronDown, FiChevronUp, FiCheck, FiSearch } from "react-icons/fi";
import "../../style/Dropdown.css";

/* Products will be fetched from API */

function CreateCustomer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = !!id;
  const crmSeed = location.state?.lead || location.state?.opportunity || null;
  const crmLeadId = location.state?.leadId ?? crmSeed?.id ?? null;
  const crmOpportunityId = location.state?.crmOpportunityId ?? null;
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(() => ({
    name: crmSeed?.customerName || "",
    mobile: crmSeed?.mobile || "",
    email: crmSeed?.email || "",
    address: crmSeed?.address || "",
    balance: "",
    reminder: "None",
    reminderEmail: false,
    reminderWhatsapp: false,
    products: [],
  }));

  const [errors, setErrors] = useState({});
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [additionalEmail, setAdditionalEmail] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Product picker (search + add row) state
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [newRowAction, setNewRowAction] = useState("Del");
  const [newRowQty, setNewRowQty] = useState("");
  const productMenuRef = useRef(null);

  // Products already added shouldn't clutter the search results again
  const availableProducts = useMemo(() => {
    return products.filter(
      (p) =>
        !form.products.some((fp) => fp.id === p.id) &&
        p.name.toLowerCase().includes(productSearchTerm.toLowerCase())
    );
  }, [products, form.products, productSearchTerm]);

  // Close the product search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (productMenuRef.current && !productMenuRef.current.contains(e.target)) {
        setIsProductMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch products from API
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.GOODS.GET_ALL}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch goods');
      }

      const goodsData = await response.json();
      console.log('Goods data:', goodsData);

      // Transform API data into the searchable catalog (rows are added on demand)
      const transformedProducts = goodsData.map((item) => ({
        id: item.id,
        name: item.name,
      }));

      setProducts(transformedProducts);

    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error("Failed to fetch products");
      
      // Set empty catalog - no hardcoded fallback
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Only allow numeric input for mobile number
    if (name === 'mobile') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setForm({ ...form, [name]: numericValue });
      
      // Real-time validation for mobile number
      let temp = { ...errors };
      if (!numericValue) {
        temp.mobile = "Required";
      } else if (!/^[6-9]\d{9}$/.test(numericValue)) {
        temp.mobile = "Valid 10-digit mobile number required";
      } else {
        temp.mobile = "";
      }
      setErrors(temp);
      return;
    }
    
    setForm({ ...form, [name]: value });
    
    // Real-time validation for email
    if (name === 'email') {
      let temp = { ...errors };
      if (value) {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
          temp.email = "Valid email required";
        } else {
          temp.email = "";
        }
      } else {
        temp.email = "";
      }
      setErrors(temp);
    }
  };

  const handleProductChange = (index, field, value) => {
    const newProducts = [...form.products];
    newProducts[index] = { ...newProducts[index], [field]: value };
    setForm({ ...form, products: newProducts });
  };

  const handleSelectProductOption = (product) => {
    setSelectedProductId(product.id);
    setProductSearchTerm(product.name);
    setIsProductMenuOpen(false);
  };

  const handleAddProductRow = () => {
    const product = products.find((p) => p.id === selectedProductId);
    if (!product) {
      toast.warning("Select a product to add");
      return;
    }

    const openingQty = newRowQty === "" || newRowQty === null || newRowQty === undefined ? 0 : Number(newRowQty);
    const finalOpeningQty = isNaN(openingQty) ? 0 : openingQty;

    setForm((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          id: product.id,
          name: product.name,
          action: newRowAction,
          qty: finalOpeningQty,
          openingBalance: finalOpeningQty,
          closingBalance: finalOpeningQty,
          batchNo: null,
          srNo: null,
          type: null,
        },
      ],
    }));

    setSelectedProductId(null);
    setProductSearchTerm("");
    setNewRowAction("Del");
    setNewRowQty("");
  };

  const handleRemoveProductRow = (index) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    let temp = {};
    temp.name = form.name ? "" : "Required";
    
    // Mobile number validation - should be 10 digits
    if (!form.mobile) {
      temp.mobile = "Required";
    } else if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      temp.mobile = "Valid 10-digit mobile number required";
    } else {
      temp.mobile = "";
    }
    
    // Email validation - only validate if email is provided
    if (form.email) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(form.email)) {
        temp.email = "Valid email required";
      } else {
        temp.email = "";
      }
    } else {
      temp.email = "";
    }
    
    temp.address = form.address ? "" : "Required";

    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = async (e, shouldNavigate = true) => {
    if (e) e.preventDefault();

    if (!validate()) {
      toast.warning("Please fill required fields correctly");
      return;
    }

    try {
      const payload = {
        name: form.name,
        contactNumber: form.mobile,
        email: form.email,
        address: form.address,
        reminder: form.reminder,
        reminderEmail: form.reminderEmail,
        reminderWhatsapp: form.reminderWhatsapp,
        deposite: Number(form.balance) || 0,
        crmLeadId: crmLeadId || null,
        crmOpportunityId: crmOpportunityId || null,
        items: form.products.map((p) => {
          const qtyNum = p.qty === "" || p.qty === null || p.qty === undefined ? 0 : Number(p.qty);
          const openBal = p.openingBalance !== undefined && p.openingBalance !== null && p.openingBalance !== "" ? Number(p.openingBalance) : (qtyNum || 0);
          const deliveredQty = p.action === "Del" ? qtyNum : 0;
          const receivedQty = p.action === "Rec" ? qtyNum : 0;
          return {
            goodsItemId: p.id, 
            name: p.name,
            deliveredQty: deliveredQty,
            receivedQty: receivedQty,
            openingBalance: Number(openBal) || 0,
            closingBalance: p.closingBalance !== undefined && p.closingBalance !== null ? Number(p.closingBalance) : (Number(openBal) || 0),
            batchNo: p.batchNo || null,
            srNo: p.srNo || null,
            type: p.type || null,
          };
        }),
      };

    
      
      const url = isEditMode
  ? `${BASE_URL}${API_ENDPOINTS.CUSTOMERS.UPDATE(id)}`
  : `${BASE_URL}/customers/save`;

const method = isEditMode ? "PUT" : "POST";

const response = await fetch(url, {
  method: method,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
  body: JSON.stringify(payload),
});

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || "Failed to create customer");
        error.response = { data: errorData, status: response.status };
        throw error;
      }

      const saved = await response.json().catch(() => ({}));
      const newCustomerId = saved?.data?.id ?? saved?.id ?? null;

      if (!isEditMode && (crmLeadId || crmOpportunityId)) {
        try {
          if (crmLeadId) {
            await crmLeadApi.update(crmLeadId, {
              status: "Converted",
              customerId: newCustomerId,
            });
          }
          if (crmOpportunityId) {
            await crmOpportunityApi.update(crmOpportunityId, {
              customerId: newCustomerId,
            });
          }
        } catch {
          /* CRM link-back is best-effort */
        }
      }

      if (shouldNavigate) {
        toast.success(isEditMode ? "Customer updated successfully" : "Customer created successfully");
        navigate("/dashboard/customers");
      } else {
        toast.success(isEditMode ? "Customer updated successfully. Form reset." : "Customer created successfully. Form reset.");
        handleReset();
      }
    } catch (err) {
      console.error("Error creating customer:", err);
      const errorMessage = getErrorMessage(err);
      toast.error(errorMessage);
      
      // Extract and set field-specific errors
      const apiFieldErrors = getFieldErrors(err);
      if (Object.keys(apiFieldErrors).length > 0) {
        const formFieldErrors = {};
        Object.entries(apiFieldErrors).forEach(([apiField, message]) => {
          const formField = mapApiFieldToFormField(apiField);
          formFieldErrors[formField] = message;
        });
        setErrors(formFieldErrors);
      }
    }
  };

  const handleSubmitAndNavigate = () => {
    handleSubmit(null, true);
  };

  const handleSubmitAndReset = () => {
    handleSubmit(null, false);
  };

  const handleReset = () => {
    setForm({
      name: "",
      mobile: "",
      email: "",
      address: "",
      balance: "",
      reminder: "None",
      reminderEmail: false,
      reminderWhatsapp: false,
      products: [],
    });
    setAdditionalEmail("");
    setErrors({});
    setSelectedProductId(null);
    setProductSearchTerm("");
    setNewRowAction("Del");
    setNewRowQty("");
  };

  const fetchCustomerById = useCallback(async (id) => {
    try {
      console.log('Fetching customer by ID:', id);
      const res = await getCustomerById(id);

      if (!res.data) throw new Error("Failed to fetch customer data");

      const data = res.data;
      console.log('Customer data received:', data);

      // Use customer items directly to build products list
      const productsFromCustomer = data.items?.map(item => ({
        id: item.goodsItemId,
        name: item.name,
        action: item.deliveredQty > item.receivedQty ? "Del" : "Rec",
        qty: item.deliveredQty > item.receivedQty ? item.deliveredQty : item.receivedQty,
        openingBalance: item.openingBalance ?? 0,
        closingBalance: item.closingBalance ?? 0,
        batchNo: item.batchNo || null,
        srNo: item.srNo || null,
        type: item.type || null,
      })) || [];

      const updatedForm = {
        name: data.name || "",
        mobile: data.contactNumber || "",
        email: data.email || "",
        address: data.address || "",
        balance: data.deposite || "",
        reminder: data.reminder || "None",
        reminderEmail: data.reminderEmail || false,
        reminderWhatsapp: data.reminderWhatsapp || false,
        products: productsFromCustomer.map((p) => {
          const existingItem = data.items?.find(
            (item) => item.goodsItemId === p.id
          );

          // Determine action based on which quantity is greater
          let action = "Rec";
          let qty = 0;

          if (existingItem) {
            if (existingItem.deliveredQty > existingItem.receivedQty) {
              action = "Del";
              qty = existingItem.deliveredQty;
            } else if (existingItem.receivedQty > existingItem.deliveredQty) {
              action = "Rec";
              qty = existingItem.receivedQty;
            } else {
              // Both are 0 or equal, default to Rec
              action = "Rec";
              qty = existingItem.deliveredQty || 0;
            }
          }

          return {
            id: p.id,
            name: p.name,
            action: action,
            qty: qty,
            // Include additional fields from API response
            openingBalance: existingItem?.openingBalance ?? 0,
            closingBalance: existingItem?.closingBalance ?? 0,
            batchNo: existingItem?.batchNo || null,
            srNo: existingItem?.srNo || null,
            type: existingItem?.type || null,
          };
        }),
      };

      console.log('Updated form:', updatedForm);
      console.log('Updated form products:', updatedForm.products);
      setForm(updatedForm);
    } catch (err) {
      console.error("Error fetching customer:", err);
      const errorMessage = getErrorMessage(err);
      toast.error(errorMessage);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchCustomerById(id);
    }
  }, [id, fetchCustomerById]);

  return (
    <div className="createcustomer-container">

      <div className="wrap">
        <div className="form-card">
          {/* Page Header */}
          <div className="page-header-section">
            <div className="header-title-group">
              <div className="header-icon">
                <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <h2>{isEditMode ? "Edit Customer" : "Create Customer"}</h2>
              <button
                type="button"
                className="back-button"
                onClick={() => navigate("/dashboard/customers")}
              >
                <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="20" width="20" xmlns="http://www.w3.org/2000/svg">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
              </button>
            </div>
            <p>{isEditMode ? "Update customer information and product preferences" : "Manage customer information and product preferences"}</p>
          </div>

        <form onSubmit={(e) => e.preventDefault()}>

          {/* === CUSTOMER INFORMATION === */}
          <div className="section">
            <h3 className="section-title">Customer Information</h3>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Customer Name <span className="required">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="Enter customer name"
                  value={form.name}
                  onChange={handleChange}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="mobile" className="form-label">
                  Mobile Number <span className="required">*</span>
                </label>
                <input
                  id="mobile"
                  type="tel"
                  name="mobile"
                  className="form-input"
                  placeholder="Enter mobile number"
                  value={form.mobile}
                  onChange={handleChange}
                />
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="Enter email address"
                  value={form.email}
                  onChange={handleChange}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="address" className="form-label">
                  Address <span className="required">*</span>
                </label>
                <input
                  id="address"
                  type="text"
                  name="address"
                  className="form-input"
                  placeholder="Enter complete address"
                  value={form.address}
                  onChange={handleChange}
                />
                {errors.address && <span className="error-message">{errors.address}</span>}
              </div>

              <div className="form-group" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={() => setShowEmailInput(!showEmailInput)}
                  className="email-toggle-btn"
                >
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {showEmailInput ? '×' : '+'}
                  </span>
                  {showEmailInput ? "Hide Email" : "Add another email"}
                </button>
              </div>

              {showEmailInput && (
                <div className="form-group">
                  <label htmlFor="additionalEmail" className="form-label">
                    Additional Email <span className="required">*</span>
                  </label>
                  <input
                    id="additionalEmail"
                    type="email"
                    name="additionalEmail"
                    className="form-input"
                    placeholder="Enter additional email address"
                    value={additionalEmail}
                    onChange={(e) => setAdditionalEmail(e.target.value)}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              )}

            </div>
          </div>

          {/* === EMAIL ACTIONS === */}
          
          {/* === FINANCIAL INFORMATION === */}
          <div className="section">
            <h3 className="section-title">Financial Information</h3>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="balance" className="form-label">
                  Opening Balance
                </label>
                <input
                  id="balance"
                  type="text"
                  name="balance"
                  className="form-input"
                  placeholder="0.00"
                  value={form.balance}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="reminder" className="form-label">
                  Reminder Settings
                </label>
                <div className="reminder-settings-container">
                  <AppDropdown
                    value={form.reminder}
                    options={[
                      { value: "None", label: "No Reminder" },
                      { value: "Daily", label: "Daily" },
                      { value: "Weekly", label: "Weekly" },
                      { value: "Monthly", label: "Monthly" }
                    ]}
                    onChange={(value) => setForm({ ...form, reminder: value })}
                    position="below"
                  />
                  <div className="reminder-toggles">
                    <div className="reminder-toggle-item">
                      <span className="reminder-toggle-label">Email</span>
                      <button
                        type="button"
                        className={`reminder-toggle-btn ${form.reminderEmail ? 'active' : ''}`}
                        onClick={() => setForm({ ...form, reminderEmail: !form.reminderEmail })}
                      >
                        {form.reminderEmail ? 'ON' : 'OFF'}
                      </button>
                    </div>
                    <div className="reminder-toggle-item">
                      <span className="reminder-toggle-label">WhatsApp</span>
                      <button
                        type="button"
                        className={`reminder-toggle-btn ${form.reminderWhatsapp ? 'active' : ''}`}
                        onClick={() => setForm({ ...form, reminderWhatsapp: !form.reminderWhatsapp })}
                      >
                        {form.reminderWhatsapp ? 'ON' : 'OFF'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* === PRODUCT PREFERENCE === */}
          <div className="section">
            <h3 className="section-title">Product Preference</h3>

            <div className="product-picker-card">
              <div className="product-picker">
                <div className="form-group">
                  <label className="form-label">Product Name</label>
                  <div className="product-dropdown-container">
                    <div
                      className="product-dropdown-input"
                      onClick={() => setIsProductMenuOpen((open) => !open)}
                    >
                      <input
                        readOnly
                        value={productSearchTerm || "Search product..."}
                        className="product-dropdown-input-field"
                        style={{
                          display: 'block',
                          flex: '1 1 auto',
                          width: '100%',
                          minWidth: 0,
                          height: '38px',
                          padding: '0 38px 0 12px',
                          margin: 0,
                          border: 0,
                          outline: 'none',
                          background: 'transparent',
                          fontSize: '13px',
                          fontWeight: 500,
                          fontFamily: 'inherit',
                          color: productSearchTerm ? '#1F2937' : '#94A3B8',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          boxSizing: 'border-box',
                          cursor: 'pointer',
                          appearance: 'none',
                          WebkitAppearance: 'none'
                        }}
                      />
                      <div className="product-dropdown-chevron">
                        {isProductMenuOpen ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                      </div>
                    </div>

                    {isProductMenuOpen && (
                      <div className="product-dropdown-menu">
                        <div style={{ position: 'relative', marginBottom: '6px' }}>
                          <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', pointerEvents: 'none' }} size={16} />
                          <input
                            type="text"
                            style={{
                              width: '100%',
                              height: '42px',
                              padding: '0 40px 0 36px',
                              boxSizing: 'border-box',
                              display: 'block',
                              borderRadius: '9px',
                              fontSize: '14px',
                              fontWeight: 500,
                              fontFamily: 'inherit',
                              color: '#1F2937',
                              background: 'transparent',
                              border: '0',
                              outline: 'none',
                              marginBottom: '6px'
                            }}
                            placeholder="Type to filter…"
                            value={productSearchTerm}
                            onChange={(e) => {
                              setProductSearchTerm(e.target.value);
                              setSelectedProductId(null);
                            }}
                            autoFocus
                          />
                        </div>
                        <div>
                          {loadingProducts ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '28px 12px', textAlign: 'center', color: '#94A3B8' }}>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>Loading products…</div>
                            </div>
                          ) : availableProducts.length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '28px 12px', textAlign: 'center', color: '#94A3B8' }}>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>No matching products</div>
                            </div>
                          ) : (
                            availableProducts.map((p) => (
                              <div
                                key={p.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: '8px',
                                  minHeight: '34px',
                                  padding: '7px 10px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  boxSizing: 'border-box',
                                  transition: 'background-color 0.12s ease',
                                  backgroundColor: selectedProductId === p.id ? '#EEF2FF' : 'transparent'
                                }}
                                onClick={() => handleSelectProductOption(p)}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#1F2937', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                                </div>
                                {selectedProductId === p.id && (
                                  <div style={{ flexShrink: 0, width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4338CA', background: '#EEF2FF' }}>
                                    <FiCheck size={14} />
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Action Type</label>
                  <AppDropdown
                    value={newRowAction}
                    options={[
                      { value: "Del", label: "Delivered" },
                      { value: "Rec", label: "Received" }
                    ]}
                    onChange={(value) => setNewRowAction(value)}
                    position="below"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Opening Qty</label>
                  <input
                    type="text"
                    min="0"
                    className="form-input quantity-input"
                    placeholder="0"
                    value={newRowQty}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "") {
                        setNewRowQty("");
                        return;
                      }
                      if (!/^\d*\.?\d*$/.test(val)) {
                        return;
                      }
                      setNewRowQty(val);
                    }}
                  />
                </div>

                <button type="button" className="btn-add-row" onClick={handleAddProductRow}>
                  <span className="btn-icon">+</span>
                  Add Row
                </button>
              </div>

              <div className="product-rows">
                {form.products.length === 0 ? (
                  <div className="empty-state">No product preferences added yet.</div>
                ) : (
                  form.products.map((prod, idx) => (
                    <div key={idx} className="product-row">
                      <span className="product-row-name">{prod.name}</span>

                      {isEditMode && (
                        <span className="product-row-meta">
                          Open {prod.openingBalance ?? 0} · Close {prod.closingBalance ?? 0}
                          {prod.batchNo ? ` · Batch ${prod.batchNo}` : ""}
                          {prod.srNo ? ` · Sr# ${prod.srNo}` : ""}
                          {prod.type ? ` · ${prod.type}` : ""}
                        </span>
                      )}

                      <AppDropdown
                        value={prod.action}
                        options={[
                          { value: "Del", label: "Delivered" },
                          { value: "Rec", label: "Received" }
                        ]}
                        onChange={(value) => handleProductChange(idx, "action", value)}
                        position="below"
                      />

                      <input
                        type="text"
                        min="0"
                        className="form-input quantity-input product-row-qty"
                        value={prod.qty}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "") {
                            const newProducts = [...form.products];
                            newProducts[idx] = { ...newProducts[idx], qty: "" };
                            setForm({ ...form, products: newProducts });
                            return;
                          }
                          if (!/^\d*\.?\d*$/.test(val)) {
                            return;
                          }
                          const newProducts = [...form.products];
                          newProducts[idx] = { ...newProducts[idx], qty: val };
                          setForm({ ...form, products: newProducts });
                        }}
                        onBlur={(e) => {
                          const val = e.target.value;
                          if (val === "" || val === null) {
                            handleProductChange(idx, "qty", 0);
                            return;
                          }
                          const num = Number(val);
                          handleProductChange(idx, "qty", isNaN(num) ? 0 : num);
                        }}
                      />

                      <button
                        type="button"
                        className="product-row-remove"
                        onClick={() => handleRemoveProductRow(idx)}
                        aria-label={`Remove ${prod.name}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* === BUTTONS === */}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleReset}>
              <span className="btn-icon">↺</span>
              Reset Form
            </button>
            <div className="action-buttons-group">
              {isEditMode && (
                <button
                  type="button"
                  className="btn btn-secondary btn-compact"
                  onClick={() => navigate("/dashboard/customers")}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="btn btn-tertiary btn-compact"
                onClick={handleSubmitAndReset}
              >
                <span className="btn-icon">↻</span>
                Save & Continue
              </button>
              <button
                type="button"
                className="btn btn-primary btn-compact"
                onClick={handleSubmitAndNavigate}
              >
                <span className="btn-icon">✓</span>
                {isEditMode ? "Update Customer" : "Create Customer"}
              </button>
            </div>
          </div>

        </form>
        </div>
      </div>
    </div>
  );
}

export default CreateCustomer;